import { PDF_PAGE_CSS, PDF_TOOLBAR_HTML, wireDocWindow } from './pdfPage';

export const LEISTUNGEN = [
    'Website-Neubau',
    'Website-Redesign',
    'Hosting / Serverbetreuung',
    'Laufende Betreuung / Wartung',
    'Automatisierung (Scripts, Tools, Workflows)',
    'Individuelle Softwareentwicklung',
    'Beratung',
];

export const EMPTY_CONTRACT_DATA = {
    auftragnehmerAnschrift: '',
    auftraggeberName: '',
    auftraggeberAnschrift: '',
    leistungen: [],
    sonstigesText: '',
    verguetungBetrag: '',
    verguetungTyp: 'netto',
    zahlungsart: 'pauschal',
    stundensatz: '',
    projektbeginn: '',
    projektfertigstellung: '',
    kuendigungsfrist: '',
    agbUrl: '',
    notizen: '',
    ortDatum: '',
};

// Werden über Verträge hinweg gemerkt (§6/§7 sind bei fast jedem Vertrag identisch)
// und bei jedem neuen Vertrag als Vorschlag vorausgefüllt, bis man sie manuell ändert.
const CONTRACT_DEFAULTS_KEY = 'business_contract_defaults';
const CONTRACT_DEFAULTS_EMPTY = { agbUrl: '', kuendigungsfrist: '' };
export const loadContractDefaults = () => {
    try { return { ...CONTRACT_DEFAULTS_EMPTY, ...JSON.parse(localStorage.getItem(CONTRACT_DEFAULTS_KEY)) }; }
    catch { return CONTRACT_DEFAULTS_EMPTY; }
};
export const saveContractDefaults = (data) => localStorage.setItem(CONTRACT_DEFAULTS_KEY, JSON.stringify({
    agbUrl: data.agbUrl || '', kuendigungsfrist: data.kuendigungsfrist || '',
}));

const escHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

const box = (checked) => checked ? '☑' : '☐';

export function buildContractHTML(data) {
    const d = { ...EMPTY_CONTRACT_DATA, ...data };
    const leistungenSet = new Set(d.leistungen || []);
    const leistungenRows = LEISTUNGEN.map(l => `<div class="check-line">${box(leistungenSet.has(l))} ${escHtml(l)}</div>`).join('');
    const sonstigesChecked = leistungenSet.has('Sonstiges');
    // Pro Absatz (durch Leerzeile getrennt) ein eigenes <p> mit page-break-inside:avoid,
    // damit der Umbruch bei langen Notizen immer zwischen zwei Punkten landet statt
    // mitten im Satz — der umgebende .section-flow-Block darf trotzdem über die Seite laufen.
    const notizenParagraphs = (d.notizen || '')
        .split(/\n+/)
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => `<p style="page-break-inside:avoid;break-inside:avoid-page;">${escHtml(s)}</p>`)
        .join('');

    return `<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>Dienstleistungsvertrag ${escHtml(d.auftraggeberName)}</title>
    <style>
        ${PDF_PAGE_CSS}
        .logo{font-family:Georgia,'Times New Roman',serif;font-size:22px;font-weight:700;color:#1a1a1a;letter-spacing:-0.3px;margin-bottom:2px;}
        .subtitle{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#666;margin-bottom:28px;}
        h1{font-size:19px;font-weight:800;margin-bottom:4px;}
        .h1sub{font-size:12px;color:#666;margin-bottom:22px;}
        .parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:24px;}
        .party{border:1px solid #ccc;border-radius:8px;padding:14px 12px;}
        .party .label{font-size:9px;text-transform:uppercase;letter-spacing:.1em;color:#666;margin-bottom:6px;}
        .party .name{font-weight:700;font-size:14px;margin-bottom:2px;}
        .party .addr{color:#555;white-space:pre-line;}
        h2{font-size:13.5px;font-weight:800;margin:22px 0 6px;padding-top:10px;border-top:1px solid #e5e7eb;page-break-after:avoid;break-after:avoid-page;}
        p{margin-bottom:6px;color:#222;orphans:3;widows:3;}
        .check-line{margin:3px 0 3px 4px;}
        .fill{font-weight:700;}
        .section{page-break-inside:avoid;break-inside:avoid-page;}
        .section-flow{page-break-inside:auto;break-inside:auto;}
        .parties{page-break-inside:avoid;break-inside:avoid-page;}
        .sig{margin-top:40px;display:grid;grid-template-columns:1fr 1fr;gap:40px;page-break-inside:avoid;break-inside:avoid-page;}
        .sig .line{border-top:1px solid #111;padding-top:6px;font-size:11px;color:#555;}
        .sig .place{font-size:11px;color:#555;margin-bottom:40px;}
    </style></head><body>
    ${PDF_TOOLBAR_HTML}
    <div class="page-wrap">
    <div id="doc-content">
    <div class="logo">&lt;MiS/&gt;</div>
    <div class="subtitle">Make it Simple</div>
    <h1>Dienstleistungsvertrag</h1>
    <div class="h1sub">über die Erbringung von IT-Dienstleistungen</div>

    <div class="parties">
        <div class="party">
            <div class="label">Auftragnehmer</div>
            <div class="name">MiS – Make it Simple</div>
            <div class="addr">${escHtml(d.auftragnehmerAnschrift) || '—'}</div>
        </div>
        <div class="party">
            <div class="label">Auftraggeber</div>
            <div class="name">${escHtml(d.auftraggeberName) || '—'}</div>
            <div class="addr">${escHtml(d.auftraggeberAnschrift) || '—'}</div>
        </div>
    </div>

    <div class="section">
    <h2>§ 1 Vertragsgegenstand</h2>
    <p>Der Auftragnehmer erbringt für den Auftraggeber folgende Leistung:</p>
    ${leistungenRows}
    ${sonstigesChecked ? `<div class="check-line">☑ Sonstiges: <span class="fill">${escHtml(d.sonstigesText) || '—'}</span></div>` : ''}
    <p style="margin-top:8px;">Sofern zu den genauen Anforderungen, dem Funktionsumfang und dem Design ein gesondertes Angebot bzw. Pflichtenheft vorliegt, wird dieses Bestandteil dieses Vertrages.</p>
    </div>

    <div class="section">
    <h2>§ 2 Mitwirkungspflichten des Auftraggebers</h2>
    <p>Der Auftraggeber stellt dem Auftragnehmer alle für die Leistungserbringung notwendigen Inhalte (Texte, Bilder, Logos, Zugangsdaten) rechtzeitig zur Verfügung und benennt einen Ansprechpartner. Verzögerungen, die durch verspätete Mitwirkung entstehen, verschieben vereinbarte Termine entsprechend.</p>
    </div>

    <div class="section">
    <h2>§ 3 Vergütung</h2>
    <p>Die Vergütung beträgt: <span class="fill">${d.verguetungBetrag ? `${escHtml(d.verguetungBetrag)} €` : '_____________________ €'}</span> (${d.verguetungTyp === 'brutto' ? 'brutto' : 'netto'}).</p>
    <div class="check-line">${box(d.zahlungsart === 'pauschal')} Preis, fällig nach Fertigstellung</div>
    <div class="check-line">${box(d.zahlungsart === 'stundensatz')} Stundensatz: <span class="fill">${d.stundensatz ? `${escHtml(d.stundensatz)} €` : '___ €'}</span> / Stunde</div>
    <p style="margin-top:8px;">Die Zahlung erfolgt innerhalb von 14 Tagen nach Rechnungsstellung ohne Abzug.</p>
    </div>

    <div class="section">
    <h2>§ 4 Termine und Fristen</h2>
    <p>Voraussichtlicher Projektbeginn: <span class="fill">${escHtml(d.projektbeginn) || '—'}</span></p>
    <p>Voraussichtliche Fertigstellung: <span class="fill">${escHtml(d.projektfertigstellung) || '—'}</span></p>
    <p style="margin-top:8px;">Verbindliche Termine werden nur wirksam vereinbart, wenn sie ausdrücklich schriftlich als „verbindlich" bezeichnet werden.</p>
    </div>

    <div class="section">
    <h2>§ 5 Nutzungs- und Urheberrechte</h2>
    <p>Mit vollständiger Bezahlung erhält der Auftraggeber die einfachen, zeitlich und räumlich unbeschränkten Nutzungsrechte an den erstellten Arbeitsergebnissen für den vereinbarten Zweck. Der Auftragnehmer darf das Projekt zu Referenzzwecken (z. B. Portfolio) nennen und darstellen, sofern der Auftraggeber nicht ausdrücklich in Textform widerspricht. Rechte an eingesetzten Fremdkomponenten (z. B. Open-Source-Bibliotheken, Fonts, Stock-Material) richten sich nach deren jeweiligen Lizenzen.</p>
    </div>

    <div class="section">
    <h2>§ 6 AGB und Datenschutz</h2>
    <p>Es gelten ergänzend die Allgemeinen Geschäftsbedingungen (AGB) sowie die Datenschutzerklärung des Auftragnehmers in ihrer jeweils gültigen Fassung, einsehbar unter: <span class="fill">${escHtml(d.agbUrl) || '—'}</span>. Beide sind Bestandteil dieses Vertrages und regeln insbesondere Haftung und Gewährleistung. Im Falle von Widersprüchen zwischen diesem Vertrag und den AGB geht dieser Vertrag vor. Der Auftraggeber bestätigt mit seiner Unterschrift, dass ihm die AGB zusammen mit diesem Vertrag ausgehändigt wurden und dass er diese zur Kenntnis genommen und akzeptiert hat.</p>
    </div>

    <div class="section">
    <h2>§ 7 Vertragslaufzeit und Kündigung</h2>
    <p>Bei Projektverträgen endet der Vertrag mit vollständiger Erbringung und Abnahme der Leistung. Bei laufender Betreuung/Wartung gilt eine Kündigungsfrist von <span class="fill">${escHtml(d.kuendigungsfrist) || '___'}</span> ${parseFloat(d.kuendigungsfrist) === 1 ? 'Monat' : 'Monaten'} zum Monatsende. Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt.</p>
    </div>

    <div class="section">
    <h2>§ 8 Schlussbestimmungen</h2>
    <p>Änderungen und Ergänzungen dieses Vertrages bedürfen der Textform. Sollte eine Bestimmung dieses Vertrages unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt. Es gilt das Recht der Bundesrepublik Deutschland.</p>
    </div>

    ${d.notizen ? `<div class="section-flow">
    <h2>§ 9 Individuelle Vereinbarungen</h2>
    ${notizenParagraphs}
    </div>` : ''}

    <div class="sig">
        <div>
            <div class="place">Ort, Datum: <span class="fill">${escHtml(d.ortDatum) || '_____________________'}</span></div>
            <div class="line">Unterschrift Auftragnehmer</div>
        </div>
        <div>
            <div class="place">Ort, Datum: <span class="fill">${escHtml(d.ortDatum) || '_____________________'}</span></div>
            <div class="line">Unterschrift Auftraggeber</div>
        </div>
    </div>
    </div>
    </div>
    <script src="${window.location.origin}/tools/html2pdf.bundle.min.js"></script>
    </body></html>`;
}

export function wireContractWindow(win, dateiname) {
    wireDocWindow(win, dateiname || 'Vertrag', ['.section', '.parties', '.sig', '.party']);
}
